import { useState, useEffect } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Loader2, Check, ArrowLeft, ArrowRight, Search, ShieldCheck, FileText, HelpCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { FranchiseeHeader } from './FranchiseeHeader';
import { ProductTermsCard, hasFranchiseeTerms, money } from './ProductTermsCard';
import { LoginPage, type LoginIntro } from '../pages/LoginPage';
import { RichText } from '../components/RichText';
import {
  acceptAdoption,
  changeFranchisee,
  declineAdoption,
  getAdoptStage,
  searchAdoptFranchisees,
  selectFranchisee,
  type EligibleFranchisee,
  type AdoptStage,
  type AdoptView,
  type ProductOption,
  type ResponseDetails,
} from '../api/adopt';

const STEPS = ['Confirm', 'Products', 'Details', 'Review', 'Decision'] as const;
const STEP_SLUGS = ['confirm', 'products', 'details', 'review', 'decision'] as const;

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-surface-canvas">
      <FranchiseeHeader />
      <main className="mx-auto max-w-3xl px-4 py-8">{children}</main>
    </div>
  );
}

function Centered({ children }: { children: React.ReactNode }) {
  return <div className="flex flex-col items-center gap-3 py-16 text-center text-graphite-600">{children}</div>;
}

/** Pre-login: show the SAME sign-in page as the rest of the app, with adoption-specific copy.
 * No intermediate "click to sign in" — the YUM_ID/OTP form is right there (PRD §10.1, §16.3). */
const ADOPT_INTRO: LoginIntro = {
  eyebrow: 'Product Adoption',
  heading: (
    <>
      Adopt your <span className="bg-byte-gradient bg-clip-text text-transparent">Byte</span> products.
    </>
  ),
  subtitle: 'You’ve been invited to review and adopt Byte products for your franchise. Sign in with your YUM ID to continue.',
};

export function AdoptionFlow() {
  const { linkId = '' } = useParams();
  const { isAuthenticated, isLoading: authLoading } = useAuth();

  const { data, isLoading, refetch } = useQuery<AdoptStage>({
    queryKey: ['adopt', 'stage', linkId],
    queryFn: () => getAdoptStage(linkId),
    enabled: isAuthenticated,
  });
  const changeMut = useMutation({ mutationFn: () => changeFranchisee(linkId), onSuccess: () => refetch() });

  if (authLoading) return <Shell><Centered><Loader2 className="h-6 w-6 animate-spin" /></Centered></Shell>;
  if (!isAuthenticated) return <LoginPage intro={ADOPT_INTRO} returnTo={`/adopt/${linkId}`} />;
  if (isLoading || !data) return <Shell><Centered><Loader2 className="h-6 w-6 animate-spin" /></Centered></Shell>;

  if (data.stage === 'unavailable') return <Shell><Centered><p>This adoption link is no longer available.</p></Centered></Shell>;
  if (data.stage === 'ineligible')
    return <Shell><Centered><p>This adoption link is for a different market and isn’t available to your franchisee.</p></Centered></Shell>;
  if (data.stage === 'no-eligible')
    return <Shell><Centered><p>No eligible franchisee was found for this market. Please contact your Byte representative.</p></Centered></Shell>;

  // Internal users (admin/legal/…) can preview the flow; a warning makes clear it's not theirs to sign.
  const banner = data.internalRole ? <InternalPreviewBanner role={data.internalRole} /> : null;

  if (data.stage === 'select')
    return <Shell>{banner}<SelectFranchisee linkId={linkId} eligible={data.eligible} searchMode={data.searchMode} onSelected={() => refetch()} /></Shell>;

  return <Shell>{banner}<Stepper linkId={linkId} view={data.view} preview={!!data.internalRole} onChangeFranchisee={() => changeMut.mutate()} /></Shell>;
}

/** Non-blocking warning shown to internal users previewing the franchisee flow. */
function InternalPreviewBanner({ role }: { role: string }) {
  return (
    <div className="mb-5 flex items-start gap-2.5 rounded-xl border border-border-subtle bg-status-warning-soft px-4 py-3 text-sm text-graphite-700">
      <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-byte-yellow" />
      <span>
        <strong>Preview mode.</strong> You’re signed in as an internal {role} user. Adoption is for franchise partners —
        you can walk the flow to preview it, but accepting and declining are disabled. <Link to="/admin" className="font-medium text-byte-purple hover:underline">Back to admin</Link>.
      </span>
    </div>
  );
}

const SEARCH_MIN_CHARS = 3;

function SelectFranchisee({
  linkId,
  eligible,
  searchMode = false,
  onSelected,
}: {
  linkId: string;
  eligible: EligibleFranchisee[];
  searchMode?: boolean;
  onSelected: () => void;
}) {
  const mutation = useMutation({ mutationFn: (id: string) => selectFranchisee(linkId, id), onSuccess: onSelected });

  // Search mode: the market roster is never shipped — the user types ≥3 characters of their entity
  // name/identifier and we query matches. Debounced so we don't search per keystroke.
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query), 250);
    return () => clearTimeout(t);
  }, [query]);
  const trimmed = debouncedQuery.trim();
  const search = useQuery({
    queryKey: ['adopt', 'fz-search', linkId, trimmed],
    queryFn: () => searchAdoptFranchisees(linkId, trimmed),
    enabled: searchMode && trimmed.length >= SEARCH_MIN_CHARS,
  });

  const options = searchMode ? (search.data ?? []) : eligible;
  const searching = searchMode && trimmed.length >= SEARCH_MIN_CHARS && search.isLoading;
  const noMatches =
    searchMode && !searching && trimmed.length >= SEARCH_MIN_CHARS && options.length === 0 && !search.isError;

  return (
    <div data-testid="adopt-select" className="mx-auto max-w-lg">
      <h1 className="text-xl font-semibold text-pretty">Choose the legal entity you are signing for</h1>
      <p className="mt-1 text-sm text-graphite-500">
        {searchMode
          ? 'Search for the franchisee entity you represent. We’ll remember it next time.'
          : 'Select the franchisee entity you represent. We’ll remember it next time.'}
      </p>
      {searchMode && (
        <div className="relative mt-5">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-graphite-400" aria-hidden="true" />
          <input
            data-testid="select-search"
            type="search"
            name="franchisee-entity"
            autoComplete="organization"
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Entity name or identifier…"
            aria-label="Search for your legal entity"
            className="w-full rounded-xl border border-border-subtle bg-white py-3 pl-9 pr-3 text-sm outline-none focus:border-byte-purple focus:ring-2 focus:ring-byte-purple/20"
          />
        </div>
      )}
      <ul className={`${searchMode ? 'mt-3' : 'mt-5'} flex list-none flex-col gap-2 p-0`}>
        {options.map((f) => (
          <li key={f.id}>
            <button
              type="button"
              data-testid={`select-${f.id}`}
              disabled={mutation.isPending}
              onClick={() => mutation.mutate(f.id)}
              className="flex min-h-11 w-full cursor-pointer items-center gap-2 rounded-xl border border-border-subtle bg-white px-4 py-3.5 text-left transition hover:-translate-y-0.5 hover:border-byte-purple/40 hover:bg-surface-warm/50 hover:shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-byte-purple/30 disabled:cursor-default disabled:opacity-50 motion-reduce:transition-none motion-reduce:hover:translate-y-0"
            >
              {mutation.isPending && mutation.variables === f.id && <Loader2 className="h-4 w-4 shrink-0 animate-spin" aria-hidden="true" />}
              <span className="min-w-0">
                <span className="block truncate text-sm font-medium text-graphite-900">{f.name}</span>
                <span className="block text-xs text-graphite-400">{f.country.toUpperCase()}</span>
              </span>
            </button>
          </li>
        ))}
      </ul>
      {searchMode && trimmed.length < SEARCH_MIN_CHARS && (
        <p className="mt-3 text-sm text-graphite-400">Type at least {SEARCH_MIN_CHARS} characters of your entity name to see matches.</p>
      )}
      {searching && <p className="mt-3 text-sm text-graphite-400" aria-live="polite">Searching…</p>}
      {noMatches && (
        <div data-testid="select-no-matches" className="mt-4 flex items-start gap-2.5" role="status">
          <HelpCircle className="mt-0.5 h-4 w-4 shrink-0 text-graphite-400" aria-hidden="true" />
          <div>
            <p className="text-sm font-medium text-graphite-700">No matching entity found</p>
            <p className="mt-1 text-sm text-graphite-500">
              Check the spelling of the legal name on record, or contact your Franchise Business Coach.
            </p>
          </div>
        </div>
      )}
      {/* Support path — always available so the select step never dead-ends (UX hub).
          Omitted when the no-matches empty state already carries the same escalation. */}
      {!noMatches && <MissingEntityHelp />}
      {(mutation.isError || search.isError) && (
        <p className="mt-3 text-sm text-status-danger" role="alert">
          {mutation.isError ? 'Couldn’t select that franchisee. Please try again.' : 'Search failed. Please try again.'}
        </p>
      )}
    </div>
  );
}

/** Offline support path when the legal entity isn’t on the roster / isn’t recognised. */
function MissingEntityHelp() {
  return (
    <aside
      data-testid="select-missing-entity"
      role="note"
      className="mt-6 flex items-start gap-2.5 border-t border-border-subtle pt-5 text-sm text-graphite-500"
    >
      <HelpCircle className="mt-0.5 h-4 w-4 shrink-0 text-graphite-400" aria-hidden="true" />
      <p>
        <span className="font-medium text-graphite-700">Don’t see your entity?</span>{' '}
        Contact your Franchise Business Coach. They can confirm the legal name on record or get it updated.
      </p>
    </aside>
  );
}

function Stepper({ linkId, view, preview = false, onChangeFranchisee }: { linkId: string; view: AdoptView; preview?: boolean; onChangeFranchisee?: () => void }) {
  const { user } = useAuth();
  const already = view.response.status === 'Accepted';
  // Step lives in the URL (?step=confirm|products|…) so it's deep-linkable and survives refresh/back.
  const [searchParams, setSearchParams] = useSearchParams();
  const stepIdx = STEP_SLUGS.indexOf((searchParams.get('step') ?? '') as (typeof STEP_SLUGS)[number]);
  const step = stepIdx === -1 ? 0 : stepIdx;
  const goToStep = (n: number) => {
    const clamped = Math.min(STEPS.length - 1, Math.max(0, n));
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      p.set('step', STEP_SLUGS[clamped]);
      return p;
    });
  };
  const [selected, setSelected] = useState<Set<string>>(new Set(view.response.selectedProducts));
  // Pre-fill the signer from the logged-in user's GIAM profile (name, job title, email).
  const [details, setDetails] = useState<ResponseDetails>(view.details);
  // Fill any blank signer fields from the logged-in user once it's available (only fills empties,
  // so it never clobbers what the signer typed). Handles the user-not-ready-at-mount race.
  useEffect(() => {
    if (!user) return;
    setDetails((d) => ({
      ...d,
      signerName: d.signerName || user.name || '',
      signerTitle: d.signerTitle || user.title || '',
      signerEmail: d.signerEmail || user.email || '',
    }));
  }, [user]);
  const [acks, setAcks] = useState<Set<string>>(new Set());
  const [authority, setAuthority] = useState(false);
  const [declining, setDeclining] = useState(false);
  const [reason, setReason] = useState('');
  const [done, setDone] = useState<null | { kind: 'accepted'; seq: number } | { kind: 'declined' }>(
    already ? { kind: 'accepted', seq: view.response.seq } : null,
  );

  const enabled = (o: ProductOption) => o.selectable && o.product.dependsOn.every((d) => selected.has(d));

  const accept = useMutation({
    mutationFn: () =>
      acceptAdoption(linkId, {
        selectedProducts: [...selected],
        details,
        authorityConfirmed: authority,
        acknowledgedProducts: [...acks],
      }),
    onSuccess: (r) => setDone({ kind: 'accepted', seq: r.response.seq }),
  });
  const decline = useMutation({
    mutationFn: () => declineAdoption(linkId, reason || undefined),
    onSuccess: () => setDone({ kind: 'declined' }),
  });

  function toggle(code: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(code)) {
        next.delete(code);
        // drop anything that depended on it
        for (const o of view.products) if (o.product.dependsOn.includes(code)) next.delete(o.product.code);
      } else {
        next.add(code);
      }
      return next;
    });
  }

  if (done?.kind === 'accepted') {
    return (
      <div data-testid="adopt-done" className="mx-auto max-w-md text-center animate-rise-in">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-status-success-soft ring-4 ring-status-success/10">
          <Check className="h-7 w-7 text-status-success" />
        </div>
        <h1 className="mt-4 text-xl font-semibold">Adoption accepted</h1>
        <p className="mt-1 text-sm text-graphite-500">Your agreement has been recorded.</p>
        <div className="mt-5 flex flex-col gap-2">
          <Link
            data-testid="view-agreement"
            to={`/agreement/${linkId}/${done.seq}`}
            className="flex items-center justify-center gap-2 rounded-xl bg-byte-gradient px-4 py-2.5 text-sm font-semibold text-white"
          >
            <FileText className="h-4 w-4" /> View &amp; print agreement
          </Link>
          <Link to="/my-adoptions" className="text-sm text-graphite-500 hover:text-graphite-700">My adoptions</Link>
        </div>
      </div>
    );
  }
  if (done?.kind === 'declined') {
    return (
      <div data-testid="adopt-declined" className="mx-auto max-w-md text-center">
        <h1 className="text-xl font-semibold">Adoption declined</h1>
        <p className="mt-1 text-sm text-graphite-500">We’ve recorded your decision. You can revisit this link any time.</p>
        <Link to="/my-adoptions" className="mt-4 inline-block text-sm text-graphite-500 hover:text-graphite-700">My adoptions</Link>
      </div>
    );
  }

  const selectedOptions = view.products.filter((o) => selected.has(o.product.code));
  const productNameByCode = new Map(view.products.map((o) => [o.product.code, o.product.name]));
  const acksNeeded = selectedOptions.filter((o) => o.product.acknowledgments.length > 0);
  const allAcked = acksNeeded.every((o) => acks.has(o.product.code));
  const canAccept = authority && selected.size > 0 && allAcked;

  // Signer details are required before leaving the Details step.
  const detailsComplete =
    details.signerName.trim() !== '' &&
    details.signerTitle.trim() !== '' &&
    /^\S+@\S+\.\S+$/.test(details.signerEmail.trim());
  // Per-step gate for the Next button.
  const canAdvance = step === 1 ? selected.size > 0 : step === 2 ? detailsComplete : true;

  return (
    <div data-testid="adopt-stepper" className="lg:grid lg:grid-cols-[200px_minmax(0,1fr)] lg:items-start lg:gap-10">
      <GuidancePanel step={step} />
      <div className="min-w-0">
      {/* stepper indicator — click a visited step to jump back (mobile/tablet; desktop uses the side panel) */}
      <ol className="mb-6 flex items-center gap-2 text-xs lg:hidden">
        {STEPS.map((label, i) => {
          const done = i < step;
          const current = i === step;
          return (
            <li key={label} className="flex flex-1 items-center gap-2 last:flex-none">
              <button
                type="button"
                onClick={() => goToStep(i)}
                disabled={i > step}
                aria-current={current ? 'step' : undefined}
                className={`flex items-center gap-1.5 ${i <= step ? 'cursor-pointer' : 'cursor-default'}`}
              >
                <span
                  className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold transition ${
                    done
                      ? 'bg-byte-purple text-white'
                      : current
                        ? 'bg-byte-purple/10 text-byte-purple ring-1 ring-byte-purple/30'
                        : 'bg-surface-muted text-graphite-400'
                  }`}
                >
                  {done ? <Check className="h-3.5 w-3.5" /> : i + 1}
                </span>
                <span className={`hidden sm:inline ${current ? 'font-semibold text-graphite-900' : 'text-graphite-400'}`}>{label}</span>
              </button>
              {i < STEPS.length - 1 && <span className={`h-px flex-1 transition-colors ${done ? 'bg-byte-purple/40' : 'bg-border-subtle'}`} aria-hidden="true" />}
            </li>
          );
        })}
      </ol>

      {step === 0 && (
        <section data-testid="step-confirm">
          <h1 className="text-xl font-semibold">Confirm your franchisee</h1>
          <p className="mt-1 text-sm text-graphite-500">Please confirm these details are correct — they come from our records.</p>
          <dl className="mt-4 grid grid-cols-2 gap-3 rounded-xl border border-border-subtle bg-white p-4 text-sm">
            <div><dt className="text-graphite-400">Legal entity</dt><dd className="font-medium">{view.franchisee.name}</dd></div>
            <div><dt className="text-graphite-400">Country</dt><dd className="font-medium">{view.franchisee.country.toUpperCase()}</dd></div>
          </dl>
          {onChangeFranchisee && (
            <button type="button" onClick={onChangeFranchisee} className="mt-3 text-sm font-medium text-byte-purple hover:underline">
              Not the right entity? Change franchisee
            </button>
          )}
        </section>
      )}

      {step === 1 && (
        <section data-testid="step-products">
          <h1 className="text-xl font-semibold">Products included in this adoption</h1>
          <p className="mt-1 text-sm text-graphite-500">Review the products below and choose the ones to adopt for {view.franchisee.name}.</p>
          <div className="mt-4 flex flex-col gap-2">
            {view.products.map((o) => {
              const isOn = selected.has(o.product.code);
              const canToggle = enabled(o) || isOn;
              return (
                <label
                  key={o.product.code}
                  data-testid={`product-${o.product.code}`}
                  className={`flex items-start gap-3 rounded-xl border p-4 transition ${
                    isOn
                      ? 'border-byte-purple/40 bg-byte-purple/[0.04] ring-1 ring-byte-purple/15'
                      : canToggle
                        ? 'cursor-pointer border-border-subtle bg-white hover:border-byte-purple/30 hover:bg-surface-warm/50'
                        : 'border-border-subtle bg-surface-muted opacity-60'
                  }`}
                >
                  <input
                    type="checkbox"
                    data-testid={`product-toggle-${o.product.code}`}
                    className="mt-1 h-4 w-4 accent-byte-purple"
                    checked={isOn}
                    disabled={!canToggle}
                    onChange={() => toggle(o.product.code)}
                  />
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">{o.product.name}</span>
                      {view.showPrices && <span className="text-xs text-graphite-500">{money(o.unitPrice)}</span>}
                    </div>
                    {view.showDescriptions && o.product.description && <RichText html={o.product.description} className="text-xs text-graphite-500" />}
                    {!enabled(o) && !isOn && o.product.dependsOn.length > 0 && (
                      <p data-testid={`product-dep-${o.product.code}`} className="mt-1 text-xs text-status-warning">
                        Requires {o.product.dependsOn.map((d) => productNameByCode.get(d) ?? d).join(', ')}
                      </p>
                    )}
                  </div>
                </label>
              );
            })}
          </div>
        </section>
      )}

      {step === 2 && (
        <section data-testid="step-details">
          <h1 className="text-xl font-semibold">Signer details</h1>
          <p className="mt-1 text-sm text-graphite-500">The signer should be authorised to accept terms on behalf of {view.franchisee.name}.</p>
          <div className="mt-4 flex flex-col gap-4 rounded-xl border border-border-subtle bg-white p-4">
            {([
              ['signerName', 'Signer name'],
              ['signerTitle', 'Signer title'],
              ['signerEmail', 'Signer email'],
            ] as const).map(([key, label]) => (
              <label key={key} className="block text-sm">
                <span className="mb-1 block font-medium text-graphite-600">{label} <span className="text-byte-red">*</span></span>
                <input
                  data-testid={`details-${key}`}
                  type={key === 'signerEmail' ? 'email' : 'text'}
                  required
                  value={details[key] ?? ''}
                  onChange={(e) => setDetails((d) => ({ ...d, [key]: e.target.value }))}
                  className="w-full rounded-lg border border-border-subtle px-3 py-2.5 outline-none focus:border-byte-purple focus:ring-2 focus:ring-byte-purple/20"
                />
              </label>
            ))}
          </div>
          {!detailsComplete && (
            <p className="mt-2 text-xs text-graphite-400">All signer details are required to continue.</p>
          )}
        </section>
      )}

      {step === 3 && (
        <section data-testid="step-review">
          <h1 className="text-xl font-semibold">Review your terms</h1>
          <p className="mt-1 text-sm text-graphite-500">Check the summary below before making a decision.</p>
          <div className="mt-4 flex flex-col gap-3">
            {selectedOptions.filter((o) => hasFranchiseeTerms(o.content)).map((o) => (
              <ProductTermsCard
                key={o.product.code}
                name={o.product.name}
                billingType={o.product.billingType}
                unitPrice={o.unitPrice}
                content={o.content}
                showPrice={view.showPrices}
              />
            ))}
            {selectedOptions.length === 0 && (
              <p className="text-sm text-graphite-500">Select at least one product to review terms.</p>
            )}
            {selectedOptions.length > 0 && selectedOptions.every((o) => !hasFranchiseeTerms(o.content)) && (
              <p className="text-sm text-graphite-500">No terms to review for the selected products.</p>
            )}
          </div>
        </section>
      )}

      {step === 4 && (
        <section data-testid="step-decision">
          <h1 className="text-xl font-semibold">Accept or decline</h1>
          <p className="mt-1 text-sm text-graphite-500">Please review your confirmation below before deciding. This is a binding acceptance of the terms on behalf of {view.franchisee.name}.</p>
          <div className="mt-4 flex flex-col gap-3">
            {acksNeeded.map((o) => (
              <label key={o.product.code} className="flex items-start gap-2.5 rounded-xl border border-border-subtle bg-white p-3 text-sm">
                <input
                  type="checkbox"
                  data-testid={`ack-${o.product.code}`}
                  className="mt-0.5 h-4 w-4 accent-byte-purple"
                  checked={acks.has(o.product.code)}
                  onChange={(e) =>
                    setAcks((prev) => {
                      const n = new Set(prev);
                      if (e.target.checked) n.add(o.product.code);
                      else n.delete(o.product.code);
                      return n;
                    })
                  }
                />
                <span>{o.product.acknowledgments.join(' ')}</span>
              </label>
            ))}
            <label className="flex items-start gap-2.5 rounded-xl border border-byte-purple/30 bg-byte-purple/[0.04] p-4 text-sm">
              <input type="checkbox" data-testid="authority" className="mt-0.5 h-4 w-4 accent-byte-purple" checked={authority} onChange={(e) => setAuthority(e.target.checked)} />
              <span className="font-medium text-graphite-800">I confirm I have authority to accept these terms on behalf of {view.franchisee.name}.</span>
            </label>

            {preview && <p className="mt-2 text-sm text-graphite-500">Preview mode — accepting and declining are disabled for internal users.</p>}
            {!declining ? (
              <div className="mt-2 flex gap-2">
                <button
                  data-testid="accept-btn"
                  disabled={!canAccept || accept.isPending || preview}
                  onClick={() => accept.mutate()}
                  className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-byte-gradient px-4 py-3 text-sm font-semibold text-white disabled:opacity-50"
                >
                  <ShieldCheck className="h-4 w-4" /> I ACCEPT
                </button>
                <button data-testid="decline-open" disabled={preview} onClick={() => setDeclining(true)} className="rounded-xl border border-border-subtle px-4 py-3 text-sm text-graphite-600 disabled:opacity-50">
                  Decline
                </button>
              </div>
            ) : (
              <div className="mt-2 rounded-xl border border-border-subtle p-3">
                <textarea
                  data-testid="decline-reason"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Reason (optional)"
                  className="w-full rounded-lg border border-border-subtle px-3 py-2 text-sm"
                />
                <div className="mt-2 flex gap-2">
                  <button data-testid="decline-submit" disabled={decline.isPending} onClick={() => decline.mutate()} className="rounded-lg bg-graphite-900 px-4 py-2 text-sm text-white disabled:opacity-50">Confirm decline</button>
                  <button onClick={() => setDeclining(false)} className="text-sm text-graphite-500">Cancel</button>
                </div>
              </div>
            )}
            {(accept.isError || decline.isError) && <p className="text-sm text-status-danger">Something went wrong. Please check your selections.</p>}
          </div>
        </section>
      )}

      {/* nav — sticky bottom bar (safe-area aware on mobile; aligns to the content column on desktop) */}
      <div className="sticky bottom-0 -mx-4 mt-8 flex items-center justify-between gap-3 border-t border-border-subtle bg-surface-canvas/95 px-4 pt-3 backdrop-blur lg:mx-0 lg:px-0 [padding-bottom:calc(0.75rem+env(safe-area-inset-bottom))]">
        <button
          data-testid="back-btn"
          onClick={() => goToStep(step - 1)}
          disabled={step === 0}
          className="flex items-center gap-1.5 rounded-lg px-1 text-sm text-graphite-500 transition hover:text-graphite-800 disabled:opacity-40 enabled:cursor-pointer"
        >
          <ArrowLeft className="h-4 w-4" /> Back
        </button>
        {step < STEPS.length - 1 && (
          <button
            data-testid="next-btn"
            onClick={() => goToStep(step + 1)}
            disabled={!canAdvance}
            className="flex items-center gap-1.5 rounded-xl bg-byte-purple px-5 py-3 text-sm font-semibold text-white shadow-sm transition hover:brightness-110 active:scale-[0.99] disabled:opacity-40 enabled:cursor-pointer"
          >
            {step === 3 ? (
              <>I ACCEPT <ArrowRight className="h-4 w-4" /></>
            ) : (
              <>Next <ArrowRight className="h-4 w-4" /></>
            )}
          </button>
        )}
      </div>
      </div>
    </div>
  );
}

/** Desktop-only left guidance panel — plain-language explanation of the steps (restrained, not a dashboard). */
function GuidancePanel({ step }: { step: number }) {
  const help = [
    'Confirm the franchisee entity.',
    'Review the products included.',
    'Capture signer details.',
    'Review the summary.',
    'Accept or decline the terms.',
  ];
  return (
    <aside className="hidden lg:block">
      <div className="sticky top-20">
        <p className="mb-3 text-xs font-semibold uppercase tracking-[0.14em] text-graphite-400">Product adoption</p>
        <ol className="space-y-3.5">
          {STEPS.map((label, i) => {
            const done = i < step;
            const current = i === step;
            return (
              <li key={label} className="flex items-start gap-2.5">
                <span
                  className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[11px] font-bold tabular-nums transition ${
                    done
                      ? 'bg-byte-purple text-white'
                      : current
                        ? 'bg-byte-purple/10 text-byte-purple ring-1 ring-byte-purple/30'
                        : 'bg-surface-muted text-graphite-300'
                  }`}
                >
                  {done ? <Check className="h-3 w-3" /> : i + 1}
                </span>
                <span>
                  <span className={`block text-sm ${current ? 'font-semibold text-graphite-900' : done ? 'font-medium text-graphite-600' : 'font-medium text-graphite-400'}`}>{label}</span>
                  <span className="block text-xs text-graphite-400">{help[i]}</span>
                </span>
              </li>
            );
          })}
        </ol>
      </div>
    </aside>
  );
}
